import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Space, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { getFinsystemNumberFormat } from 'src/libs/utils'
import { CryptoIcon } from 'src/pages/finSystem/wallets/walletsTable/CryptoIcon'
import { useExDifferenceList } from 'src/pages/finSystem/balanceSummary/reports/reportExDifference/hook'
import {
  Difference,
  DifferenceFilter,
} from 'src/pages/finSystem/balanceSummary/reports/reportExDifference/types'
import { SortDirectionT } from 'src/pages/finSystem/balanceSummary/reports/types'
import styles from 'src/pages/finSystem/balanceSummary/reports/styles.module.css'

type Props = {
  sortDirection: SortDirectionT
  setSortDirection: Dispatch<SetStateAction<SortDirectionT>>
  setFilters: (values: Partial<DifferenceFilter>) => void
}

export const ExDifferenceListTable = ({
  sortDirection,
  setSortDirection,
  setFilters,
}: Props) => {
  const [leftOffset, setLeftOffset] = useState(0)
  const { exDifferenceList, loading } = useExDifferenceList()

  const amountUsdRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const { Text } = Typography

  const columns: ColumnsType<Difference> = [
    {
      title: 'Wallet ID',
      dataIndex: 'walletId',
    },
    {
      title: 'Wallet',
      dataIndex: 'walletName',
      render: (name) => (
        <Space>
          <CryptoIcon name={name} size={30} /> {name}
        </Space>
      ),
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
    },
    {
      title: <div ref={amountUsdRef}>Сумма курсовой разницы, USD</div>,
      dataIndex: 'exchangeDiffAmountUsd',
      sorter: true,
      sortOrder: sortDirection,
      render: (amount) => (
        <>
          <Text
            className={amount >= 0 ? 'text-success' : 'text-danger'}
            style={{ fontSize: 16 }}
          >
            {amount > 0
              ? `+${getFinsystemNumberFormat(amount, 2)}`
              : getFinsystemNumberFormat(amount, 2)}
          </Text>{' '}
          <Text type="secondary" style={{ fontSize: 16 }}>
            USD
          </Text>
        </>
      ),
    },
  ]
  const total = exDifferenceList?.totals.totalExchangeDiffAmountUsd
    ? String(exDifferenceList?.totals.totalExchangeDiffAmountUsd)
    : 0

  useEffect(() => {
    const updateOffset = () => {
      if (!amountUsdRef.current || !wrapperRef.current) return

      const headerRect = amountUsdRef.current.getBoundingClientRect()
      const wrapperRect = wrapperRef.current.getBoundingClientRect()

      setLeftOffset(headerRect.left - wrapperRect.left)
    }

    updateOffset()

    const resizeObserver = new ResizeObserver(updateOffset)
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [exDifferenceList])

  return (
    <div ref={wrapperRef}>
      <div className={`even-row ${styles.summery}`}>
        <div>Total Amount</div>
        <div style={{ position: 'absolute', left: leftOffset, bottom: 16 }}>
          {getFinsystemNumberFormat(total, 2)} USD
        </div>
      </div>

      <Table
        dataSource={exDifferenceList?.items}
        loading={loading}
        columns={columns}
        onChange={(p, f, sorter) => {
          const sort = Array.isArray(sorter) ? sorter[0] : sorter
          const { order, field } = sort

          if (!order && !field) {
            setFilters({ sorterField: undefined })
          }
          if (field === 'exchangeDiffAmountUsd') {
            const sorterField =
              order === 'descend'
                ? '-exchangeDiffAmountUsd'
                : 'exchangeDiffAmountUsd'
            setFilters({ sorterField })
          }

          setSortDirection(order)
        }}
        size="large"
        scroll={{ x: true }}
        rowKey={(record) => `${record.walletId}_ ${record.currency}`}
        pagination={{
          hideOnSinglePage: true,
          position: ['bottomCenter'],
        }}
        className={styles.row}
        rowClassName={(_, index) => `${index % 2 ? 'even-row' : 'odd-row'}`}
      />
    </div>
  )
}
